import { MouseEventHandler } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import { match } from 'ts-pattern'
import { Spinner } from '@/components/common/etc'
import { Typo, TypoVariant } from '@/components/common/typography'
import DownloadCouponCard from '@/components/domains/coupon/DownloadCouponCard'
import { Z_INDEXES } from '@/constants/legacy/constLayout'
import { EventContainer } from '@/containers/contexts/regional/event.container'
import useEventDetailEventTracker from '@/containers/event-tracker/event-detail.event-tracker'
import { UserContainer } from '@/containers/users/UserContainer'
import { useAdCouponGroupsQuery, useDownloadEventCouponsMutation } from '@/graphql/generated/hooks'
import { CouponGroupCannotIssueReasonEnum, CouponGroupType } from '@/graphql/generated/schemas'
import { doSetGlobalToastContent } from '@/stores/reduxUI'
import {
  checkExcludedCoupon,
  ExcludedCouponTypes,
  IncludedCouponTypes,
  makeCouponDisplayData,
  makeCouponIncludeTargetString,
} from '@/utils/coupon.util'

const text = '쿠폰 다운받기'

interface Props {
  isModalOpen: boolean
  closeModal: () => void
}

const getCanNotIssueReasonText = (reason?: CouponGroupCannotIssueReasonEnum) => {
  return match(reason)
    .with(CouponGroupCannotIssueReasonEnum.OnlyMembership, () => '멤버십 전용 쿠폰은 멤버십 회원만 사용이 가능해요')
    .with(CouponGroupCannotIssueReasonEnum.OnlyFirstOrder, () => '이 쿠폰은 첫 구매 회원만 사용이 가능해요')
    .otherwise(() => '')
}

const EventCouponDownloadModal = ({ isModalOpen, closeModal }: Props) => {
  const { me } = UserContainer.useContainer()
  const dispatch = useDispatch()
  const { ad } = EventContainer.useContainer()
  const { data, refetch, loading } = useAdCouponGroupsQuery({
    skip: !me,
    variables: {
      slug: ad.slug ?? '',
    },
  })

  const [downloadCoupon] = useDownloadEventCouponsMutation({
    errorPolicy: 'none',
    onCompleted: () => {
      refetch()
      dispatch(
        doSetGlobalToastContent({
          content: '쿠폰이 발급되었어요',
        })
      )
    },

    onError: (error) => {
      let errorMessage = '일시적인 오류가 발생했어요. 잠시 후 다시 시도해 주세요.' // 기본 폴백(Fallback) 메시지

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const rawMessage = error.graphQLErrors[0].message

        try {
          const parsedMessage = JSON.parse(rawMessage)

          if (parsedMessage.message) {
            errorMessage = parsedMessage.message
          }
        } catch (e) {
          errorMessage = rawMessage
        }
      } else if (error.networkError) {
        errorMessage = '네트워크 연결 상태를 확인해 주세요.'
      }

      dispatch(
        doSetGlobalToastContent({
          content: errorMessage,
        })
      )
    },
  })
  const handleClickModalOutside = () => {
    closeModal()
  }

  const handleClickModalInside: MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation()
  }

  const couponGroups = data?.adBySlug?.couponGroups?.edges.map((e) => e.node)
  const mainElement = typeof document !== 'undefined' && document.querySelector('#main')

  const { trackerClickEventsaleCouponDownload } = useEventDetailEventTracker()

  const handleCouponDownload = async ({
    id,
    couponName,
    canIssue,
    isDisableCoupon,
  }: {
    id: CouponGroupType['id']
    canIssue: CouponGroupType['canIssue']
    couponName: CouponGroupType['name']
    isDisableCoupon: boolean
  }) => {
    if (!canIssue || (canIssue && isDisableCoupon)) {
      return
    }

    trackerClickEventsaleCouponDownload({
      adId: ad.id,
      adName: ad.name,
      adSlug: ad.slug || '',
      couponGroupId: id,
      couponName: couponName,
    })

    await downloadCoupon({
      variables: {
        input: {
          couponGroup: id,
        },
      },
    })
  }

  return mainElement && isModalOpen
    ? createPortal(
        <StyledModal onClick={handleClickModalOutside}>
          <StyledModalContent className={isModalOpen ? 'enter' : ''} onClick={handleClickModalInside}>
            <StyledModalHeader>
              <Typo variant={TypoVariant.Heading6Bold}>{text}</Typo>
            </StyledModalHeader>
            <StyledModalBody>
              {loading ? (
                <StyledSpinner>
                  <Spinner />
                </StyledSpinner>
              ) : (
                couponGroups?.map((couponGroup) => {
                  if (!couponGroup) return
                  const {
                    id,
                    name,
                    canConjunctUse,
                    canIssue,
                    isMembershipCoupon,
                    includedBrands,
                    includedCategories,
                    includedProducts,
                    excludedBrands,
                    excludedCategories,
                    excludedProducts,
                    canNotIssueReason,
                  } = couponGroup

                  const { title, amount, datePeriodForDownload } = makeCouponDisplayData(couponGroup)

                  const couponCoverageContent = makeCouponIncludeTargetString({
                    includedBrands,
                    includedCategories,
                    includedProducts,
                  } as IncludedCouponTypes)

                  const isExcludeCouponCoverage = checkExcludedCoupon({
                    excludedBrands,
                    excludedCategories,
                    excludedProducts,
                  } as ExcludedCouponTypes)

                  // canNotIssueReason 값이 없고 있어도 hasCoupon이 아닌 경우 다운로드 가능
                  const isAvailableDownload =
                    !canNotIssueReason && canNotIssueReason !== CouponGroupCannotIssueReasonEnum.HasCoupon
                  // 사용 불가 사유가 아닌 쿠폰
                  const isAvailableReason =
                    canNotIssueReason !== CouponGroupCannotIssueReasonEnum.OnlyMembership &&
                    canNotIssueReason !== CouponGroupCannotIssueReasonEnum.OnlyFirstOrder

                  return (
                    <DownloadCouponCard
                      key={id}
                      title={title}
                      isMembership={!!isMembershipCoupon}
                      isConjunctUse={canConjunctUse}
                      name={name}
                      amount={amount}
                      datePeriod={`${datePeriodForDownload} 다운 가능`}
                      couponCoverageContent={couponCoverageContent}
                      isExcludeCouponCoverage={isExcludeCouponCoverage}
                      isAvailableDownload={isAvailableDownload}
                      onClick={() =>
                        handleCouponDownload({
                          id,
                          couponName: name,
                          canIssue,
                          isDisableCoupon: !isAvailableReason,
                        })
                      }
                      disabled={!isAvailableReason}
                      errorMessage={getCanNotIssueReasonText(canNotIssueReason)}
                    />
                  )
                })
              )}
            </StyledModalBody>
          </StyledModalContent>
        </StyledModal>,
        mainElement
      )
    : null
}
const StyledModal = styled.div`
  position: sticky;
  bottom: 0;
  z-index: ${Z_INDEXES.BOTTOM_SLIDE_POPUP};
  display: flex;
  align-items: flex-end;
  width: min(100%, ${(props) => props.theme.deviceSize.tablet});
  height: 100dvh;
  background-color: rgba(0, 0, 0, 0.5);
`

const slideIn = keyframes`
  0% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(0%);
  }
`

const StyledModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 63vh;
  height: auto;
  overflow: hidden;

  &.enter {
    animation: ${slideIn} 0.3s ease-in-out;
  }
`

const StyledModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1.6rem;
  border-radius: 2rem 2rem 0 0;
  background-color: ${({ theme }) => theme.color.grayWhite};
`

const StyledModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 0.8rem;
  min-height: 16rem;
  width: 100%;
  padding: 1rem 1.6rem 1.6rem 1.6rem;
  background-color: ${({ theme }) => theme.color.grayWhite};
  overflow-y: scroll;
`

const StyledSpinner = styled.div`
  margin-top: 2.6rem;
`

export default EventCouponDownloadModal
